import { ChangeDetectionStrategy, Component, Input, OnInit } from '@angular/core';
import { DataNoRowOverlayComponent } from '@examdojo/ag-grid';
import { PageType, PageVersionStoreModel, TopicStatusToUI } from '@examdojo/category/v2';
import { DateTimeFormat, DateTimeService } from '@examdojo/core/date-time';
import { ListViewGridMessages, MasterListViewComponent, provideSimpleListView } from '@examdojo/core/list-view';
import { UnwrapArray, UnwrapObservable } from '@examdojo/core/typescript';
import { IonContent, IonHeader, IonTitle, IonToolbar, ModalController } from '@ionic/angular/standalone';
import { ColDef, GridOptions, RowClickedEvent } from 'ag-grid-community';
import { IconButtonComponent } from '@examdojo/core/icon-button';
import { PAGE_VERSION_BROWSER_DIALOG_ID } from './page-version-browser-dialog.service';
import { PageVersionBrowserListViewService } from './page-version-browser-list-view.service';

type ColData = UnwrapArray<UnwrapObservable<PageVersionBrowserListViewService['data$']>>;

@Component({
  selector: 'exam-page-version-browser',
  imports: [IonHeader, IonTitle, IonToolbar, MasterListViewComponent, IonContent, IconButtonComponent],
  templateUrl: './page-version-browser.component.html',
  styleUrl: './page-version-browser.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [provideSimpleListView(PageVersionBrowserListViewService)],
})
export class PageVersionBrowserComponent implements OnInit {
  constructor(
    private readonly pageVersionBrowserListViewService: PageVersionBrowserListViewService,
    private readonly dateTimeService: DateTimeService,
    private readonly modalCtrl: ModalController,
  ) {}

  @Input({ required: true }) pageType!: PageType;
  @Input() activeId?: PageVersionStoreModel['id'];

  readonly columnDefs: Array<ColDef<ColData>> = [
    {
      headerName: 'Updated At',
      field: 'updated_at' satisfies keyof ColData,
      sort: 'desc',
      valueFormatter: ({ data }) => {
        if (!data?.updated_at) {
          return '';
        }
        return this.dateTimeService.formatDateRelativeToNow(data.updated_at);
      },
      tooltipValueGetter: ({ data }) => {
        if (!data?.updated_at) {
          return '';
        }
        return this.dateTimeService.formatDate(data.updated_at, DateTimeFormat.ShortDateTime);
      },
    },
    {
      headerName: 'Current',
      field: 'isActive' satisfies keyof ColData,
      valueFormatter: (params) => {
        return params.value ? '✅' : '';
      },
    },
    {
      field: 'page_status' satisfies keyof ColData,
      headerName: 'Status',
      valueGetter: ({ data }) => {
        return TopicStatusToUI[data!.page_status].label;
      },
    },
  ];

  readonly gridOptions: GridOptions<ColData> = {
    // TODO handle row click
    suppressCellFocus: true,
    noRowsOverlayComponent: DataNoRowOverlayComponent,
    noRowsOverlayComponentParams: {
      text: 'No version found',
      body: 'Please try with another filter',
    },
    defaultColDef: {
      sortable: true,
      resizable: true,
      suppressMovable: true,
      menuTabs: [],
    },
    context: {
      componentParent: this,
    },
    animateRows: true,
    rowSelection: 'single',
    onRowClicked: (event: RowClickedEvent<ColData>) => {
      this.closeModal(event.data!.id);
    },
    getContextMenuItems: () => [],
    rowClass: 'cursor-pointer',
  };

  readonly messages: ListViewGridMessages = {
    noDataHeader: 'No page version for this tab',
    noDataBody: 'There is no page version available for this tab',
  };

  ngOnInit() {
    this.pageVersionBrowserListViewService.pageType.set(this.pageType);
    this.pageVersionBrowserListViewService.activeId.set(this.activeId ?? null);
  }

  closeModal(selectedPageVersionId?: PageVersionStoreModel['id']) {
    this.modalCtrl.dismiss(selectedPageVersionId, '', PAGE_VERSION_BROWSER_DIALOG_ID);
  }
}
